/* Lyn-ID -- one screen, one tap, an answer.
 *
 * WHAT THIS IS FOR
 * ----------------
 * Somebody is standing in a wood holding something. They do not yet know
 * whether it is a mushroom, a herb or a berry -- and that is precisely the
 * problem with the app's existing doors, which ask you to pick the category
 * before you can ask the question. This screen asks nothing. The camera is up
 * on arrival, one tap sends the frame, and the answer covers the screen.
 *
 * It sits BESIDE `MushroomIdentify`, `BatchIdentify` and `Foraging` -- none of
 * them are touched. Those screens do more (several angles, extra notes,
 * browsing by species); this one does less on purpose.
 *
 * THE CAMERA, AND WHY THERE ARE TWO OF THEM
 * -----------------------------------------
 * The fast path is an in-page viewfinder via getUserMedia: the shutter is a
 * real single tap. The fallback is the platform camera through a file input,
 * which costs three taps (open, shoot, confirm) but works absolutely
 * everywhere -- desktop browsers, an Android WebView without the CAMERA
 * permission, a locked-down work profile, or anyone who simply says no to the
 * permission prompt. The fallback is not an error state and is not drawn as
 * one; it is just the slower of two working doors.
 *
 * THE SAFETY POSITION
 * -------------------
 * Making this faster also makes it faster to be confidently wrong about
 * something you are about to eat. The rules that keep that in check are NOT
 * in this file: the server enforces the warning and the edibility vocabulary
 * in `services/identify_any.py`, and the client's own floor under them lives
 * in `lib/quickId.js` where tests can hold it still. This file draws what
 * those two decide.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Camera, Image as ImageIcon, Loader2, X, RotateCcw, AlertTriangle,
    ShieldAlert, Skull, Leaf, ArrowRight, WifiOff, Lock,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { api } from '../lib/api';
import { fileToCompressedDataURL } from '../lib/image';
import { setVolumeShutter, VOLUME_SHUTTER_EVENT } from '../lib/nativePhone';
import LogFindModal from '../components/LogFindModal';
import SnapShortcutControls from '../components/SnapShortcutControls';
import './QuickIdentify.css';
import { useAuth } from '../contexts/AuthContext';
import useOnline from '../lib/useOnline';
import { offlineFinds, offlineSaveError } from '../lib/offlineFinds';
import { cameraErrorMessage, findIdentificationLabel, findIdentificationStatus } from '../lib/wildFinds';
import { toneFor, warningFor, confidenceFor, stateFromError, dataUrlToBase64, isAnswer, translated } from '../lib/quickId';

const MAX_EDGE = 1024;
const QUALITY = 0.82;

// Hardware keys that act as a shutter while the viewfinder is up: volume
// rocker (forwarded by the Android app), camera key and selfie-stick remotes.
const SHUTTER_KEYS = new Set(['AudioVolumeUp', 'AudioVolumeDown', 'VolumeUp', 'VolumeDown', 'Camera']);

function dataUrlToFile(dataUrl, name = 'find.jpg') {
    try {
        const [head, body] = dataUrl.split(',');
        const mime = (head.match(/data:([^;]+)/) || [])[1] || 'image/jpeg';
        const bytes = atob(body);
        const buf = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
        return new File([buf], name, { type: mime });
    } catch { return null; }
}

const TONE_STYLE = {
    calm: { ring: 'border-brand-green/40', chip: 'bg-brand-green/15 text-brand-green border-brand-green/40',
        box: 'bg-brand-green/10 border-brand-green/40 text-brand-ink', Icon: Leaf },
    caution: { ring: 'border-brand-ochre/50', chip: 'bg-brand-ochre/15 text-brand-ochre border-brand-ochre/40',
        box: 'bg-brand-ochre/10 border-brand-ochre/50 text-brand-ink', Icon: ShieldAlert },
    danger: { ring: 'border-red-400', chip: 'bg-red-100 text-red-700 border-red-300',
        box: 'bg-red-50 border-red-300 text-red-900', Icon: Skull },
};

const DANGER_CHIP = {
    deadly: 'bg-red-100 text-red-700 border-red-300',
    toxic: 'bg-orange-100 text-orange-700 border-orange-300',
    harmless: 'bg-brand-bg2 text-brand-ink3 border-brand-border',
};

export default function QuickIdentify() {
    const { user, offlineOwner, loading } = useAuth();
    if (loading) return <div role="status" className="p-8">GrowDaily…</div>;
    return <QuickIdentifyContent key={user?.user_id || offlineOwner || 'device'} />;
}

function QuickIdentifyContent() {
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const { user, offlineOwner, loading: authLoading } = useAuth();
    const owner = user?.user_id || offlineOwner || 'device';
    const ownerRef = useRef(owner); ownerRef.current = owner;
    const online = useOnline();
    const [draft, setDraft] = useState(null);
    const [storageError, setStorageError] = useState('');

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const cameraRequestRef = useRef(0);
    const analysisInFlight = useRef(false);
    // To inputs, ikke ét. `capture` tvinger systemkameraet frem, så den ene
    // knap kan ikke både åbne kameraet og åbne albummet -- med kun ét input
    // åbnede galleri-knappen kameraet igen, hvilket er den ene ting den ikke
    // skulle.
    const cameraRef = useRef(null);
    const albumRef = useRef(null);
    // Nothing may call setState after the screen is gone: the network request
    // outlives the component whenever somebody backs out mid-analysis.
    const aliveRef = useRef(true);

    // Tre tilstande, ikke to. Med et boolsk `live` er "vi spørger lige nu"
    // ikke til at skelne fra "brugeren sagde nej", og skærmen kom til at sige
    // "kameraet er ikke tilladt her" mens tilladelsesdialogen stadig stod
    // åben -- altså påstå et afslag der ikke var sket. Det er det allerførste
    // en ny bruger ser af funktionen.
    const [camera, setCamera] = useState('starting');   // starting | live | blocked
    const [cameraError, setCameraError] = useState('');
    const [busy, setBusy] = useState(false);
    const [shot, setShot] = useState(null);
    const [result, setResult] = useState(null);
    const [failure, setFailure] = useState(null);
    const [logOpen, setLogOpen] = useState(false);
    const shotFile = useMemo(() => (shot ? dataUrlToFile(shot) : null), [shot]);
    // Opened from "Snap a find" (map, home-screen shortcut): logging is the goal.
    const logMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('then') === 'log';

    const stopStream = useCallback(() => {
        cameraRequestRef.current += 1;
        setVolumeShutter(false);
        const stream = streamRef.current;
        streamRef.current = null;
        if (stream) {
            // Leaving tracks running leaves the phone's camera indicator on
            // after the user has moved on, which reads as the app spying.
            try { stream.getTracks().forEach((track) => track.stop()); } catch { /* ignore */ }
        }
        if (videoRef.current) videoRef.current.srcObject = null;
    }, []);

    const startStream = useCallback(async () => {
        if (streamRef.current) return;
        const attempt = ++cameraRequestRef.current;
        const media = typeof navigator !== 'undefined' && navigator.mediaDevices;
        if (!media || !media.getUserMedia) { setCamera('blocked'); setCameraError(cameraErrorMessage(null, locale === 'da')); return; }
        setCamera('starting');
        setCameraError('');
        try {
            const stream = await media.getUserMedia({
                video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
                audio: false,
            });
            if (attempt !== cameraRequestRef.current || !aliveRef.current || document.hidden || !viewfinderRef.current) { stream.getTracks().forEach((track) => track.stop()); return; }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                try { await videoRef.current.play(); } catch { /* autoplay policy; the poster still shows */ }
            }
            if (attempt !== cameraRequestRef.current || !aliveRef.current) return;
            setCamera('live');
            // Android resets its hardware-key flag on pause even when React's
            // canShoot state is unchanged. Reapply it after every foreground start.
            setVolumeShutter(viewfinderRef.current);
        } catch (error) {
            if (attempt !== cameraRequestRef.current || !aliveRef.current) return;
            // Denied, unavailable, or an insecure origin. The file input below
            // is a complete answer to all three, so this is not an error -- but
            // it IS now a real refusal, which is the only point at which the
            // screen is entitled to say so.
            setCamera('blocked');
            setCameraError(cameraErrorMessage(error, locale === 'da'));
        }
    }, [locale]);

    useEffect(() => {
        aliveRef.current = true;
        startStream();
        return () => { aliveRef.current = false; stopStream(); };
    }, [startStream, stopStream]);

    // Er den blotte soeger fremme lige nu? Holdes i en ref, saa lytteren
    // nedenfor ikke skal gentegnes ved hver tilstandsaendring.
    const viewfinderRef = useRef(true);

    // Slip kameraet naar appen ikke er paa skaermen.
    //
    // Afmontering er ikke den eneste maade en soeger holder op med at blive
    // kigget paa -- paa en telefon er det den sjaeldne. Trykker man hjem eller
    // laaser skaermen, lever WebView'en og denne komponent videre, og uden det
    // her bliver stroemmen ved med at koere i lommen med kameraindikatoren
    // taendt. Det ser ud praecis som det ser ud: en app der kigger med.
    //
    // Testen for afmontering fangede det ikke, fordi den testede det forkerte
    // tidspunkt.
    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) { stopStream(); return; }
            // Kun den blotte soeger genoptages. Et frosset billede, et svar
            // eller en fejl paa skaermen betyder at kameraet ikke er det
            // brugeren kigger paa, og at aabne det igen bag et kort er ikke
            // noget nogen har bedt om.
            if (viewfinderRef.current) startStream();
        };
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', stopStream);

        // Capacitor melder app-tilstand mere paalideligt end visibilitychange
        // goer i aeldre WebViews. Begge dele, for stopStream er idempotent.
        let handle;
        const App = typeof window !== 'undefined' && window.Capacitor?.Plugins?.App;
        if (App?.addListener) {
            const sub = App.addListener('appStateChange', ({ isActive }) => {
                if (!isActive) { stopStream(); return; }
                if (viewfinderRef.current) startStream();
            });
            Promise.resolve(sub).then((h) => { handle = h; }).catch(() => {});
        }

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', stopStream);
            try { handle?.remove?.(); } catch { /* ignore */ }
        };
    }, [startStream, stopStream]);

    // Attach whatever stream exists to whatever <video> is currently mounted.
    //
    // Doing this only inside `startStream` is a race. `again()` clears the
    // frozen frame and restarts the camera in the same tick, so the <video> is
    // still unmounted -- videoRef is null -- when getUserMedia is called, and
    // whether it has remounted by the time the promise resolves depends on
    // scheduling. Lose that race and the stream never reaches the element: a
    // black viewfinder with a shutter that captures nothing, silently, and
    // only sometimes. No dependency array on purpose; the guard below makes
    // every run after the first attach a no-op.
    useEffect(() => {
        const video = videoRef.current;
        const stream = streamRef.current;
        if (!video || !stream || video.srcObject === stream) return;
        video.srcObject = stream;
        Promise.resolve(video.play?.()).catch(() => { /* autoplay policy */ });
    });

    const keepOffline = useCallback(async (dataUrl) => {
        const file = dataUrlToFile(dataUrl);
        if (!file) { setStorageError(offlineSaveError(null, locale === 'da')); return; }
        try {
            const row = await offlineFinds.save(draft?.owner === 'device' ? 'device' : owner, { id: draft?.id, files: draft?.files || [file], payload: draft?.payload || { category: 'other', species_name: locale === 'da' ? 'Ukendt fund' : 'Unidentified find' } });
            if (aliveRef.current && ownerRef.current === owner) { setDraft(row); setStorageError(''); }
        } catch (error) { if (aliveRef.current) setStorageError(offlineSaveError(error, locale === 'da')); }
    }, [owner, draft, locale]);

    const send = useCallback(async (dataUrl) => {
        if (analysisInFlight.current) return;
        analysisInFlight.current = true;
        viewfinderRef.current = false;
        setShot(dataUrl);
        setResult(null);
        setFailure(null);
        setBusy(true);
        stopStream();
        try {
            if (navigator.onLine === false) {
                await keepOffline(dataUrl);
                if (aliveRef.current) setFailure({ kind: 'offline' });
                return;
            }
            const r = await api.post('/ai/identify-any', {
                image_base64: dataUrlToBase64(dataUrl),
                locale,
            }, { timeout: 120000 });
            if (!aliveRef.current) return;
            if (isAnswer(r.data)) {
                setResult(r.data);
            } else {
                // 200 er ikke det samme som et svar. Se `isAnswer`.
                setFailure({ kind: 'error', message: '' });
            }
        } catch (err) {
            if (!err?.response && err?.code !== 'ERR_CANCELED' && ownerRef.current === owner) await keepOffline(dataUrl);
            if (aliveRef.current) setFailure(stateFromError(err));
        } finally {
            analysisInFlight.current = false;
            if (aliveRef.current) setBusy(false);
        }
    }, [locale, stopStream, keepOffline, owner]);

    const resumeId = new URLSearchParams(window.location.search).get('draft');
    useEffect(() => {
        if (!resumeId || authLoading) return;
        let active = true;
        viewfinderRef.current = false; stopStream();
        offlineFinds.get(owner, resumeId).then(row => row || offlineFinds.get('device', resumeId)).then(row => {
            if (!active) return;
            if (!row?.files?.[0]) { setStorageError(locale === 'da' ? 'Det lokale foto kunne ikke findes på denne enhed og konto. Åbn Offline-fund eller tag et nyt foto.' : 'The local photo could not be found on this device and account. Open Offline finds or take a new photo.'); return; }
            const reader = new FileReader();
            reader.onload = () => { if (active) { setDraft(row); setShot(reader.result); setFailure({ kind: 'saved' }); } };
            reader.readAsDataURL(row.files[0]);
        }).catch(error => { if (active) setStorageError(offlineSaveError(error, locale === 'da')); });
        return () => { active = false; };
    }, [resumeId, owner, authLoading, stopStream, locale]);

    const shoot = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const scale = Math.min(1, MAX_EDGE / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        send(canvas.toDataURL('image/jpeg', QUALITY));
    }, [send]);

    // Volume keys take the picture while the bare viewfinder is up. The
    // Android app only forwards them while this flag is on, so everywhere
    // else they keep changing the volume as usual.
    const canShoot = camera === 'live' && !shot && !busy && !result && !failure;
    useEffect(() => {
        if (!canShoot) return undefined;
        setVolumeShutter(true);
        const fire = () => shoot();
        const onKey = (e) => { if (SHUTTER_KEYS.has(e.key)) { e.preventDefault(); shoot(); } };
        window.addEventListener(VOLUME_SHUTTER_EVENT, fire);
        window.addEventListener('keydown', onKey);
        return () => {
            setVolumeShutter(false);
            window.removeEventListener(VOLUME_SHUTTER_EVENT, fire);
            window.removeEventListener('keydown', onKey);
        };
    }, [canShoot, shoot]);

    const onFile = useCallback(async (event) => {
        const file = (event.target.files || [])[0];
        event.target.value = '';
        if (!file) return;
        try {
            send(await fileToCompressedDataURL(file, { maxWidth: MAX_EDGE, quality: QUALITY }));
        } catch { /* unreadable file; the shutter is still there */ }
    }, [send]);

    const again = useCallback(() => {
        viewfinderRef.current = true;
        setResult(null);
        setFailure(null);
        setShot(null);
        setDraft(null); setStorageError('');
        setCamera('starting');
        startStream();
    }, [startStream]);

    const leave = useCallback(() => {
        stopStream();
        navigate(logMode ? '/finds-map' : '/dashboard');
    }, [navigate, stopStream, logMode]);

    // A cancelled price dialog is the user's own decision, not a failure, so
    // it puts the camera straight back rather than showing them a card.
    useEffect(() => {
        if (failure && failure.kind === 'cancelled') again();
    }, [failure, again]);

    const live = camera === 'live';
    const showSheet = busy || !!result || (!!failure && failure.kind !== 'cancelled');
    // Opdateres efter hver render, saa baggrundslytteren ovenfor kan se hvad
    // der er fremme uden selv at skulle gentegnes.
    viewfinderRef.current = !shot && !showSheet;

    if (authLoading) return <div role="status" className="p-8">{locale === 'da' ? 'Åbner kamera…' : 'Opening camera…'}</div>;

    return (
        <div className="fixed inset-0 bg-neutral-950 text-white flex flex-col" data-testid="quickid-page">
            {/* The viewfinder, or the frozen frame once one is taken. */}
            <div className="absolute inset-0 overflow-hidden">
                {shot
                    ? <img src={shot} alt="" className="w-full h-full object-cover opacity-50" data-testid="quickid-shot" />
                    : (
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            autoPlay
                            data-testid="quickid-video"
                            className={`w-full h-full object-cover transition-opacity duration-300 ${live ? 'opacity-100' : 'opacity-0'}`}
                        />
                    )}
                {!live && !shot && <div className="absolute inset-0 bg-neutral-900" />}
            </div>

            <header className="snap-camera-header relative z-10 flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+1rem)] pb-3 bg-gradient-to-b from-black/60 to-transparent">
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-white/60">{t('quickid.eyebrow')}</p>
                    <h1 className="font-serif text-xl">{t('quickid.title')}</h1>
                </div>
                <button
                    type="button"
                    onClick={leave}
                    data-testid="quickid-close"
                    aria-label={t('quickid.close')}
                    className="p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                >
                    <X size={18} />
                </button>
            </header>

            {!showSheet && <aside className="snap-camera-shortcuts relative z-10 px-5 pb-2" aria-label={t('phone.fast_title')}>
                <SnapShortcutControls prefix="quickid" camera includeTile />
            </aside>}
            {!showSheet && !online && <p role="status" className="relative z-10 mx-5 p-3 rounded-2xl bg-black/70 text-sm">{locale === 'da' ? 'Offline · Snap et fund. Fotoet gemmes på telefonen til senere.' : 'Offline · Snap a find. Your photo will be saved on this device for later.'}</p>}
            {!showSheet && <button className="relative z-10 mx-5 text-sm underline text-left" onClick={() => navigate('/finds-map')}>{locale === 'da' ? 'Åbn mine offline-fund' : 'Open my offline finds'}</button>}

            <div className="flex-1" />

            {/* The shutter. One tap when the viewfinder is live; the platform
                camera otherwise. Both are always reachable. */}
            {!showSheet && (
                <div className="snap-camera-shutter relative z-10 px-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] bg-gradient-to-t from-black/70 to-transparent pt-10">
                    <p className="text-center text-xs text-white/70 mb-4" data-testid="quickid-hint">
                        {camera === 'live' ? `${t('quickid.hint')} · ${t('quickid.volume_hint')}`
                            : camera === 'starting' ? t('quickid.starting')
                            : cameraError || t('quickid.camera_blocked')}
                    </p>
                    {camera === 'blocked' && <button type="button" data-testid="quickid-camera-retry" onClick={startStream} className="block mx-auto text-xs underline text-white/80 mb-3">{locale === 'da' ? 'Prøv livekamera igen' : 'Retry live camera'}</button>}
                    <div className="flex items-center justify-center gap-6">
                        <button
                            type="button"
                            onClick={() => albumRef.current?.click()}
                            data-testid="quickid-pick"
                            aria-label={t('quickid.use_photo')}
                            className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition-colors"
                        >
                            <ImageIcon size={20} strokeWidth={1.8} />
                        </button>
                        {live ? (
                            <button
                                type="button"
                                onClick={shoot}
                                data-testid="quickid-shutter"
                                aria-label={t('quickid.shutter')}
                                className="w-20 h-20 rounded-full bg-white ring-4 ring-white/30 active:scale-95 transition-transform flex items-center justify-center"
                            >
                                <span className="w-16 h-16 rounded-full bg-brand-green" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => cameraRef.current?.click()}
                                data-testid="quickid-open-camera"
                                className="px-6 py-4 rounded-full bg-brand-green hover:bg-brand-greenDark font-medium inline-flex items-center gap-2 transition-colors"
                            >
                                <Camera size={18} strokeWidth={1.8} /> {t('quickid.open_camera')}
                            </button>
                        )}
                        <span className="w-12 h-12" aria-hidden="true" />
                    </div>
                </div>
            )}

            {/* Kameraet: `capture` sender brugeren direkte i systemkameraet. */}
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onFile}
                className="hidden"
                data-testid="quickid-file"
            />
            {/* Albummet: INGEN `capture`. Attributten er hele forskellen. */}
            <input
                ref={albumRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                className="hidden"
                data-testid="quickid-album"
            />

            <LogFindModal
                open={logOpen}
                onClose={() => setLogOpen(false)}
                defaultKind={['mushroom', 'fungus'].includes(result?.kind) ? 'mushroom' : 'wild'}
                defaultSpecies={{ name: result?.common_name || result?.scientific_name || draft?.payload?.species_name || (locale === 'da' ? 'Ukendt fund' : 'Unidentified find'), kind: result?.kind || 'other' }}
                initialPhotos={draft?.files || (shotFile ? [shotFile] : null)}
                initialIdentification={result}
                initialDraft={draft}
                onLogged={() => { stopStream(); navigate('/finds-map'); }}
            />

            {showSheet && (
                <div className="relative z-10 mt-auto max-h-[88vh] overflow-y-auto bg-brand-card text-brand-ink rounded-t-3xl border-t border-brand-border p-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] animate-fade-up">
                    {busy && <Working label={t('quickid.analysing')} />}
                    {storageError && <p role="alert" className="mb-3 text-sm text-brand-terracotta">{storageError}</p>}
                    {draft && <p role="status" className="mb-3 text-sm text-brand-green">{locale === 'da' ? 'Foto gemt på telefonen. Tilføj noter/GPS nedenfor, eller find det igen under Offline-fund.' : 'Photo saved on this device. Add notes/GPS below, or find it again under Offline finds.'}</p>}
                    {!busy && shot && !result && online && <button className="w-full py-3 mb-3 rounded-full bg-brand-green text-white" onClick={() => send(shot)}>{locale === 'da' ? 'Identificér dette foto med AI' : 'Identify this photo with AI'}</button>}
                    {!busy && result && <Answer result={result} t={t} da={locale === 'da'} onAgain={again} onLeave={leave} navigate={navigate} logMode={logMode} onLog={() => setLogOpen(true)} />}
                    {!busy && !result && failure && <>{(failure.kind !== 'saved') ? <Failure state={failure} t={t} onAgain={again} navigate={navigate} /> : <button type="button" onClick={again} className="w-full py-3 rounded-full border border-brand-border">{locale === 'da' ? 'Tag et nyt foto' : 'Take another photo'}</button>}<button type="button" data-testid="quickid-log-unidentified" onClick={() => setLogOpen(true)} className="w-full mt-4 py-3 rounded-full border border-brand-border text-sm inline-flex items-center justify-center gap-2"><Lock size={15} />{locale === 'da' ? 'Gem foto privat uden artsbestemmelse' : 'Save photo privately without identification'}</button></>}
                </div>
            )}
        </div>
    );
}

function Working({ label }) {
    return (
        <div className="py-8 flex flex-col items-center gap-3 text-brand-ink3" data-testid="quickid-busy">
            <Loader2 size={24} className="animate-spin text-brand-green" />
            <p className="text-sm">{label}</p>
        </div>
    );
}

function Answer({ result, t, da, onAgain, onLeave, navigate, logMode, onLog }) {
    const tone = toneFor(result);
    const style = TONE_STYLE[tone] || TONE_STYLE.caution;
    const ToneIcon = style.Icon;
    const confidence = confidenceFor(result);
    const warning = warningFor(result, t('quickid.fallback_warning'));
    const lookalikes = Array.isArray(result.lookalikes) ? result.lookalikes : [];
    const steps = Array.isArray(result.next_steps) ? result.next_steps : [];
    const unknown = t('quickid.unknown');
    const kindLabel = translated(t, `quickid.kind.${result.kind}`, unknown);
    const edibilityLabel = translated(t, `quickid.edibility.${result.edibility}`, unknown);

    return (
        <div className="space-y-4" data-testid="quickid-result" data-tone={tone}>
            <p data-testid="quickid-observation-status" className="text-xs text-brand-ink2">{findIdentificationLabel(findIdentificationStatus(result), da)} · {da ? 'Ikke en garanti for spiselighed eller medicinsk brug.' : 'Not a guarantee of edibility or medical use.'}</p>
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${style.chip}`} data-testid="quickid-kind">
                        <ToneIcon size={11} /> {kindLabel}
                    </span>
                    {confidence !== null && (
                        <span className="text-[11px] text-brand-ink3" data-testid="quickid-confidence">
                            {confidence}% {t('quickid.confidence')}
                        </span>
                    )}
                </div>
                <h2 className="font-serif text-2xl text-brand-ink">{result.common_name || t('quickid.unknown')}</h2>
                {result.scientific_name && <p className="italic text-sm text-brand-ink3">{result.scientific_name}</p>}
            </div>

            {result.summary && <p className="text-sm text-brand-ink leading-relaxed">{result.summary}</p>}

            <div className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${style.box}`} data-testid="quickid-warning">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed font-medium">{warning}</p>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-brand-border bg-brand-bg2 text-brand-ink" data-testid="quickid-edibility">
                {edibilityLabel}
            </div>

            {lookalikes.length > 0 && (
                <div className="bg-brand-bg2 border border-brand-border rounded-2xl p-4" data-testid="quickid-lookalikes">
                    <h3 className="text-xs uppercase tracking-widest font-semibold text-brand-ink3 mb-3">{t('quickid.lookalikes')}</h3>
                    <ul className="space-y-3">
                        {lookalikes.map((item) => (
                            <li key={item.name}>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-brand-ink">{item.name}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${DANGER_CHIP[item.danger] || DANGER_CHIP.toxic}`}>
                                        {translated(t, `quickid.danger.${item.danger}`, t('quickid.danger.toxic'))}
                                    </span>
                                </div>
                                {item.how_to_tell && <p className="text-xs text-brand-ink2 mt-0.5 leading-relaxed">{item.how_to_tell}</p>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {steps.length > 0 && (
                <div data-testid="quickid-steps">
                    <h3 className="text-xs uppercase tracking-widest font-semibold text-brand-ink3 mb-2">{t('quickid.next_steps')}</h3>
                    <ul className="text-sm text-brand-ink space-y-1">
                        {steps.map((step) => (
                            <li key={step} className="flex items-start gap-2">
                                <ArrowRight size={13} className="flex-shrink-0 mt-1 text-brand-ink3" />
                                <span>{step}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button
                type="button"
                onClick={onLog}
                data-testid="quickid-log-find"
                className={`w-full py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 transition-colors ${logMode ? 'bg-brand-ochre text-brand-ink hover:opacity-90' : 'bg-brand-bg2 border border-brand-border text-brand-ink hover:border-brand-green'}`}
            >
                <Lock size={15} /> {da ? 'Gem i min private fundlog' : 'Save to my private find log'}
            </button>
            <p className="text-[11px] text-brand-ink3 -mt-2 text-center">{da ? 'Foto og artsforslag følges ad. Placering er valgfri; intet deles nu.' : 'Photo and species suggestion stay together. Location is optional; nothing is shared now.'}</p>

            <div className="flex gap-3 pt-1">
                <button
                    type="button"
                    onClick={onAgain}
                    data-testid="quickid-again"
                    className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium inline-flex items-center justify-center gap-2 transition-colors"
                >
                    <RotateCcw size={15} /> {t('quickid.retake')}
                </button>
                <button
                    type="button"
                    onClick={onLeave}
                    data-testid="quickid-done"
                    className="px-6 bg-brand-bg2 hover:bg-brand-bg border border-brand-border text-brand-ink py-3 rounded-full font-medium transition-colors"
                >
                    {t('quickid.close')}
                </button>
            </div>
            <button
                type="button"
                onClick={() => navigate('/foraging')}
                className="w-full text-[11px] text-brand-ink3 hover:text-brand-ink underline"
            >
                {t('wild.title')}
            </button>
        </div>
    );
}

function Failure({ state, t, onAgain, navigate }) {
    // The mushroom gate is the one failure that is really an answer: the server
    // knows what it is looking at and is withholding the name on purpose. It
    // keeps the warning, and it does NOT get the generic error card.
    if (state.kind === 'premium_mushroom') {
        return (
            <div className="space-y-4" data-testid="quickid-premium">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-brand-ochre/15 text-brand-ochre border-brand-ochre/40">
                        <Lock size={11} /> {t('quickid.kind.fungus')}
                    </span>
                </div>
                <h2 className="font-serif text-2xl text-brand-ink">{t('quickid.premium_title')}</h2>
                {state.warning && (
                    <div className="rounded-2xl border-2 border-red-300 bg-red-50 text-red-900 p-4 flex items-start gap-3" data-testid="quickid-premium-warning">
                        <Skull size={16} className="flex-shrink-0 mt-0.5" />
                        <p className="text-sm leading-relaxed font-medium">{state.warning}</p>
                    </div>
                )}
                {state.message && <p className="text-sm text-brand-ink2">{state.message}</p>}
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/premium')}
                        data-testid="quickid-premium-cta"
                        className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium transition-colors"
                    >
                        {t('quickid.premium_cta')}
                    </button>
                    <button type="button" onClick={onAgain} className="px-6 bg-brand-bg2 border border-brand-border text-brand-ink py-3 rounded-full font-medium">
                        {t('quickid.retake')}
                    </button>
                </div>
            </div>
        );
    }

    const CARDS = {
        quota: { Icon: Lock, title: t('quickid.quota_title'), body: t('quickid.quota_body'),
            cta: t('quickid.quota_cta'), go: '/premium' },
        signin: { Icon: Lock, title: t('quickid.signin_title'), body: '',
            cta: t('quickid.signin_cta'), go: '/login' },
        offline: { Icon: WifiOff, title: t('quickid.offline_title'), body: t('quickid.offline_body'), cta: '', go: '' },
        not_deployed: { Icon: AlertTriangle, title: t('quickid.notdeployed_title'),
            body: t('quickid.notdeployed_body'), cta: '', go: '' },
        error: { Icon: AlertTriangle, title: t('quickid.error_title'), body: state.message || '', cta: '', go: '' },
    };
    const card = CARDS[state.kind] || CARDS.error;
    const CardIcon = card.Icon;

    return (
        <div className="space-y-4 py-2" data-testid={`quickid-failure-${state.kind}`}>
            <div className="flex items-start gap-3">
                <CardIcon size={20} className="text-brand-ochre flex-shrink-0 mt-0.5" />
                <div>
                    <h2 className="font-serif text-xl text-brand-ink">{card.title}</h2>
                    {card.body && <p className="text-sm text-brand-ink2 mt-1 leading-relaxed">{card.body}</p>}
                </div>
            </div>
            <div className="flex gap-3">
                {card.cta && (
                    <button
                        type="button"
                        onClick={() => navigate(card.go)}
                        data-testid="quickid-failure-cta"
                        className="flex-1 bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium transition-colors"
                    >
                        {card.cta}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onAgain}
                    data-testid="quickid-failure-again"
                    className={`${card.cta ? 'px-6' : 'flex-1'} bg-brand-bg2 hover:bg-brand-bg border border-brand-border text-brand-ink py-3 rounded-full font-medium transition-colors`}
                >
                    {t('quickid.try_again')}
                </button>
            </div>
        </div>
    );
}
